/**
 * Routines guide anchors: the controls a person uses to create a routine and
 * run it once, on the Routines page and in the create dialog, and how the
 * console knows each one is done. Kernel: shared/guideAnchor.ts; registry and
 * catalogue: shared/guideAnchorRegistry.ts. The page marks each control with
 * guideAnchor(name) (frontend/src/platform/guide/guideAnchor.js); the dialog
 * marks its controls only when it creates, so editing a routine ticks
 * nothing.
 *
 * Help lines are the texts the page shows next to each control; the page
 * renders them from here (guideAnchorHelp), so the page and Guide never
 * disagree. Only the brief has one.
 *
 * Creating and running are read back from the routines list the page already
 * loads: a routine of the person's own that was not listed when the tour
 * started is the one they created, and it has run once the list shows a run
 * for it. The dialog's own steps are observed on the page (the dialog open,
 * a field filled).
 */

import { defineGuideAnchors, defineGuideStateReads, type GuidePage } from '../guideAnchor'
import { contracts } from './routines.contracts'
import { identity } from './routines'

interface ListedRoutine {
  readonly id?: unknown
  readonly readonly?: unknown
  readonly last_run_id?: unknown
}

function listed(data: unknown): readonly ListedRoutine[] {
  const routines = (data as { routines?: unknown } | null | undefined)?.routines
  return Array.isArray(routines) ? routines.filter((routine): routine is ListedRoutine => Boolean(routine) && typeof routine === 'object') : []
}

export const guideStateReads = defineGuideStateReads(identity, contracts, {
  'routines.list.created': {
    label: 'A routine of your own that is new since the guide started',
    contract: 'list',
    items: (data) => listed(data).filter((routine) => routine.readonly !== true && routine.id != null).map((routine) => String(routine.id)),
  },
  'routines.list.has-run': {
    label: 'The routine has run',
    contract: 'list',
    keyed: true,
    done: (data, key) => listed(data).some((routine) => String(routine.id) === key && routine.last_run_id != null),
  },
})

const page: GuidePage = { path: '/routines' }

export const guideAnchors = defineGuideAnchors(identity, guideStateReads, {
  'routines.list.new': {
    label: 'New Routine',
    page,
    completion: { kind: 'visible', anchor: 'routines.create.dialog' },
  },
  'routines.create.dialog': {
    label: 'Create Routine dialog',
    page,
    completion: { kind: 'visible' },
  },
  'routines.create.brief': {
    label: 'Brief',
    page,
    help: 'Describe your goals in plain language. The AI will generate a structured prompt.',
    completion: { kind: 'filled' },
  },
  'routines.create.generate': {
    label: 'Generate Prompt',
    page,
    completion: { kind: 'filled', anchor: 'routines.create.prompt' },
  },
  'routines.create.prompt': {
    label: 'Prompt',
    page,
    completion: { kind: 'filled' },
  },
  'routines.create.save': {
    label: 'Create Routine',
    page,
    completion: { kind: 'state', read: 'routines.list.created' },
  },
  'routines.list.run': {
    label: 'Run routine now',
    page,
    keyed: true,
    completion: { kind: 'state', read: 'routines.list.has-run' },
  },
})
